/**
 * The level of the access hierarchy a Check Access page is rendered for. Deliberately not called "scope": in
 * Identity/AuthZ a *scope* is a permission grant (OAuth scope, role assignment scope path); this is only the page's
 * position in the org → tenant hierarchy. A folder is never a page level — it is a selection made *within* a tenant
 * page, which is why it appears in `EffectiveAccessScopeType` (the API's vocabulary) but not here.
 */
export type CheckAccessLevel = 'Organization' | 'Tenant';

/**
 * What a Check Access page is looking at.
 * - Organization: `id` is the organization (partition) global id, `name` the organization name.
 * - Tenant: `id` is the tenant id, `name` the tenant name.
 */
export interface ICheckAccessTarget {
    level: CheckAccessLevel;
    id: string;
    name: string;
}

/** `ScopeType` values accepted by `/pap_/api/geteffectiveaccess`. */
type EffectiveAccessScopeType = 'Organization' | 'Tenant' | 'Folder';

interface IEffectiveAccessScopeIdentifier {
    ScopeType: EffectiveAccessScopeType;
    Value: {
        Id: string;
        ParentId?: string;
    };
}

/**
 * Builds the `ScopeIdentifier` payload for the `/pap_/api/geteffectiveaccess` call.
 * - Organization: no tenant exists, so only the organization id is sent.
 * - Tenant: the tenant id (also as `ParentId`, as the API expects).
 * - Tenant + `folderKey`: the folder, with the tenant as its parent. Folders only exist under a tenant, so a
 *   `folderKey` is ignored at organization level.
 */
export function buildScopeIdentifier(target: ICheckAccessTarget, folderKey?: string): IEffectiveAccessScopeIdentifier {
    switch (target.level) {
        case 'Organization':
            return {
                ScopeType: 'Organization',
                Value: { Id: target.id },
            };
        case 'Tenant':
            return folderKey
                ? {
                    ScopeType: 'Folder',
                    Value: {
                        Id: folderKey,
                        ParentId: target.id,
                    },
                }
                : {
                    ScopeType: 'Tenant',
                    Value: {
                        Id: target.id,
                        ParentId: target.id,
                    },
                };
    }
}

export const isOrganizationLevel = (target?: ICheckAccessTarget) => target?.level === 'Organization';
